import { useReduxState } from 'hooks';
import { User } from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  CompetencyMatrixUsersListCompetencyMatch,
  CompetencyMatrixUsersListProfileMatch
} from '.';

import type { GetUsersForCompetencyMatrixRes } from 'shared-types/responses';
import type { CompetencyInMatrix } from 'lib/competencies/getCompetencyMatrixColumnsByView';
import type { CompetencyMatrixView } from './CompetencyMatrix';

interface Props {
  columns: CompetencyInMatrix[];
  users: GetUsersForCompetencyMatrixRes['users'];
  view?: CompetencyMatrixView;
}

const CompetencyMatrixUsersList = ({
  columns,
  users,
  view = 'competencies'
}: Props) => {
  const { messages } = useReduxState();

  return (
    <BoxOverflowWrapper withStickyHeader={true}>
      <BoxTable>
        <BoxTableHeader
          sticky={true}
          verticalAlign="bottom"
          withCheckbox={true}
          withCheckboxIdentifier="users-page-competency-matrix"
        >
          <BoxTableCell sticky={true} verticalAlign="bottom">
            {messages.commonUser}
          </BoxTableCell>

          {columns?.map((column, i) => (
            <BoxTableCell
              key={i}
              tooltip={`${column.title}${
                column.levelTitle ? `: ${column.levelTitle}` : ''
              }`}
              verticalAlign="bottom"
              verticalText={true}
              verticalTextMaxHeight={150}
            >
              {column.levelTitle && (
                <div className="title level">{column.levelTitle}</div>
              )}
              <div className="title">{column.title}</div>
            </BoxTableCell>
          ))}
        </BoxTableHeader>

        {users.map((user) => (
          <BoxTableRow key={user.id} withCheckboxValue={user.id}>
            <BoxTableCell sticky={true} stickyBackgroundColor="white">
              <User
                enableProfileLink={true}
                style={{ paddingRight: 20, minWidth: 200 }}
                user={user}
              />
            </BoxTableCell>

            {columns?.map((column, i) => (
              <BoxTableCell
                key={`${column.value}${column.level ? `-${column.level}` : ''}`}
                style={{ textAlign: 'center' }}
              >
                {view === 'competencies' ? (
                  <CompetencyMatrixUsersListCompetencyMatch
                    competency={column}
                    user={user}
                    viewLevelsSeparately={true}
                  />
                ) : view === 'competency-levels' ? (
                  <CompetencyMatrixUsersListCompetencyMatch
                    competency={column}
                    user={user}
                  />
                ) : (
                  <CompetencyMatrixUsersListProfileMatch
                    profile={column}
                    user={user}
                  />
                )}
              </BoxTableCell>
            ))}
          </BoxTableRow>
        ))}
      </BoxTable>

      <style jsx>{`
        .title {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .level {
          font-weight: 400;
        }
      `}</style>
    </BoxOverflowWrapper>
  );
};

export default CompetencyMatrixUsersList;
